export type HudEventCategory = 'critical' | 'objective' | 'combat' | 'support' | 'system';

export type HudPriorityMeta = {
  category: HudEventCategory;
  priority: number;
  label: string;
  tone: string;
};

export type HudEventLike = {
  id?: string;
  message?: string;
};

export type PrioritizedHudEvent<T extends HudEventLike> = {
  event: T;
  message: string;
  index: number;
  score: number;
  meta: HudPriorityMeta;
};

const CATEGORY_META: Record<HudEventCategory, Omit<HudPriorityMeta, 'category'>> = {
  critical: { priority: 480, label: 'Critical', tone: '#ff8d74' },
  objective: { priority: 360, label: 'Objective', tone: '#62ddff' },
  combat: { priority: 250, label: 'Combat', tone: '#ffcc66' },
  support: { priority: 170, label: 'Support', tone: '#8ef0c9' },
  system: { priority: 90, label: 'System', tone: '#b58cff' },
};

function normalizedMessage(message: string) {
  return message.toLowerCase();
}

function eventCategory(message: string): HudEventCategory {
  const text = normalizedMessage(message);
  if (
    /critical|breach|destroyed|offline|carrier pressure|captured the|base under/.test(text)
  ) {
    return 'critical';
  }

  if (
    /flag|route|directive|defend order|attack order|escort order|regroup order|generator/.test(text)
  ) {
    return 'objective';
  }

  if (
    /blocked shot|shot blocked|blocked shots?|deflect(?:ed)?|ricochet|impact|hit|killed|eliminated|damage|overheat(?:ed)?/.test(
      text,
    )
  ) {
    return 'combat';
  }

  if (/loaded|equipped|repair|inventory|station|loadout/.test(text)) {
    return 'support';
  }

  return 'system';
}

export function classifyHudEventMessage(message: string): HudPriorityMeta {
  const category = eventCategory(message);
  const meta = CATEGORY_META[category];
  return {
    category,
    priority: meta.priority,
    label: meta.label,
    tone: meta.tone,
  };
}

export function prioritizeHudEvents<T extends HudEventLike>(
  events: T[],
  maxCount: number,
): PrioritizedHudEvent<T>[] {
  const safeMax = Math.max(0, Math.floor(maxCount));
  if (safeMax === 0) {
    return [];
  }

  return events
    .map((event, index) => {
      const message = event.message?.trim() || 'Unknown event';
      const meta = classifyHudEventMessage(message);
      const recency = index * 0.8;
      return {
        event,
        message,
        index,
        score: meta.priority + recency,
        meta,
      };
    })
    .sort((left, right) => right.score - left.score || right.index - left.index)
    .slice(0, safeMax);
}
